<template>
    <v-card flat class="booking-card" :class="modal ? 'modal-card' : 'static-card'" v-if="days.length > 0">
        <v-card-title v-if="currentPhase == 'form'">
            <v-row>
                <v-col cols="12" class="pb-0" v-if="modal">
                    <span class="text-h5 mb-2">Book a Viewing</span>
                </v-col>

                <v-col cols="12" :class="modal ? 'pt-0' : ''">
                    <small class="vd-block">
                        To complete your booking, we just need a few details.
                    </small>

                    <v-alert
                        text
                        dense
                        outlined
                        :icon="false"
                        width="100%"
                        color="primary"
                        type="info"
                        class="mt-5"
                        >
                        <v-row align="center">
                            <v-col class="grow">
                                <small>
                                    You have selected
                                    <a @click="currentPhase = 'timepicker'"
                                    class="font-weight-medium text-decoration-underline">{{ time }}</a>
                                    on {{ formattedDate }}
                                </small>
                            </v-col>

                            <v-col class="shrink">
                                <v-btn color="primary"
                                    outlined
                                    small
                                    @click="modal ? changeDate() : currentPhase = 'timepicker'"
                                    :disabled="bookingLoading"
                                >
                                    Change
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-alert>
                </v-col>
            </v-row>
        </v-card-title>

        <v-card-title v-if="currentPhase == 'timepicker' || currentPhase == 'datepicker' || currentPhase == 'confirmBooking'">
            <span v-if="!time && modal" class="text-h5">Select a {{ date ? 'time' : 'date' }}</span>
            <span v-else-if="modal" class="text-h5">Confirm Booking</span>
        </v-card-title>

        <v-card-title v-if="currentPhase == 'success'">
            <span class="text-h5 mb-2">Booking Complete</span>
        </v-card-title>


        <v-card-text v-if="currentPhase == 'success'">
            <v-container>
                <v-row>
                    <v-alert
                        prominent
                        text
                        outlined
                        width="100%"
                        color="success"
                        type="success"
                        >
                        <v-row align="center">
                            <v-col class="grow">
                                You're booked in! Your viewing is at
                                {{ time }} on {{ formattedDate }}
                            </v-col>
                        </v-row>
                    </v-alert>
                </v-row>
            </v-container>
        </v-card-text>

        <v-card-text>
            <v-container fluid v-if="currentPhase == 'datepicker' || currentPhase == 'timepicker' || currentPhase == 'confirmBooking'">
                <v-row class="picker-row" justify="start">
                    <v-col v-if="currentPhase == 'datepicker' || inlineSelectors"
                        cols="12" :md="inlineSelectors ? 6 : 12">
                        <v-date-picker
                            v-model="date"
                            no-title
                            :allowed-dates="days ? allowedDates : null"
                            class="mt-4 mb-4"
                            :elevation="modal ? 2 : 12"
                            raised
                            color="primary lighten-1"
                            full-width
                            :min="minDate"
                        ></v-date-picker>
                        <v-alert
                            v-if="errors && errors.slot"
                            type="error"
                        >
                            The selected date/time is invalid, please choose a different date/time.
                        </v-alert>
                    </v-col>

                    <v-col v-if="currentPhase == 'timepicker' || inlineSelectors"
                        cols="12" :md="inlineSelectors ? 5 : 12" :offset-md="inlineSelectors ? 1 : 0">
                        <v-col class="px-0 pb-0" cols="12">
                            <h3 class="color-primary">{{ listingName }}</h3>
                            <p class="mb-1">{{ listingLocation }}</p>

                            <strong v-if="date">
                                Selected date {{ formattedDate }}

                                <v-icon v-if="modal"
                                    color="primary"
                                    small class="ml-1"
                                    @click="changeDate()">
                                    mdi-calendar-edit
                                </v-icon>
                            </strong>
                        </v-col>

                        <v-carousel v-if="times.length > 0"
                            :height="235"
                            :show-arrows="chunkSlots(date).length > 1"
                            hide-delimiters
                            v-model="carouselPage"
                            class="timeslot-slider">
                            <v-carousel-item v-for="(chunks, i) in chunkSlots(date)" :key="i">
                                <v-col cols="12" class="py-2 px-0" v-for="slot in chunks" :key="slot.id">
                                    <v-row align="center">
                                        <v-col>{{ slot.slotTime }} - {{ getTimeslotEnd(slot) }}</v-col>

                                        <v-col class="text-end">
                                            <v-btn @click="selectTime(slot)"
                                                color="primary">
                                                Book
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-carousel-item>
                        </v-carousel>
                    </v-col>
                </v-row>

                <v-row v-if="currentPhase == 'confirmBooking'">
                    <v-col cols="12" class="mt-5 px-0">
                        <v-alert
                            prominent
                            text
                            outlined
                            width="100%"
                            color="primary"
                            type="info"
                            icon="mdi-calendar-clock"
                            >
                            <v-row align="center">
                                <v-col class="grow">
                                    You have selected

                                    <a @click="currentPhase = 'timepicker'"
                                    class="font-weight-medium text-decoration-underline">{{ time }}</a>

                                    on {{ formattedDate }}
                                </v-col>

                                <v-col class="shrink">
                                    <v-btn color="primary"
                                        outlined
                                        @click="modal ? changeDate() : currentPhase = 'timepicker'"
                                        :disabled="bookingLoading"
                                    >
                                        Change
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-alert>

                        <small class="text-muted">
                            Appointment should last approximately {{ openDay.duration }} minutes
                        </small>

                        <v-alert
                            v-if="errors && errors.appointment"
                            prominent
                            text
                            outlined
                            width="100%"
                            color="danger"
                            type="error"
                            class="mt-4"
                        >
                            <v-row align="center">
                                <v-col class="grow">
                                    {{ errors.appointment[0] }}
                                </v-col>
                            </v-row>
                        </v-alert>

                        <v-alert
                            v-if="error"
                            prominent
                            text
                            outlined
                            width="100%"
                            color="danger"
                            type="error"
                            >
                            <v-row align="center">
                                <v-col class="grow">
                                    Oops! Something went wrong.
                                    Please try again later.
                                </v-col>
                            </v-row>
                        </v-alert>
                    </v-col>
                </v-row>
            </v-container>

            <v-container v-if="currentPhase == 'form'">
                <v-row>
                    <v-col cols="12" class="grow px-0">
                        <v-form v-model="valid">
                            <v-text-field
                                v-model="formFields.firstName"
                                ref="firstNameField"
                                label="First Name *"
                                :rules="rules.firstName"
                                class="rounded-0"
                                required
                                outlined
                            ></v-text-field>
                            <v-alert
                                v-if="errors && errors.firstName"
                                type="error"
                            >{{ errors.firstName[0] }}</v-alert>

                            <v-text-field
                                v-model="formFields.lastName"
                                ref="lastNameField"
                                label="Last Name *"
                                :rules="rules.lastName"
                                class="rounded-0"
                                required
                                outlined
                            ></v-text-field>
                            <v-alert
                                v-if="errors && errors.lastName"
                                type="error"
                            >{{ errors.lastName[0] }}</v-alert>

                            <v-text-field
                                v-model="formFields.email"
                                ref="emailField"
                                label="Email *"
                                :rules="rules.email"
                                :readonly="emailDisabled"
                                :disabled="emailDisabled"
                                class="rounded-0"
                                required
                                outlined
                            ></v-text-field>
                            <v-alert
                                v-if="errors && errors.email"
                                type="error"
                            >{{ errors.email[0] }}</v-alert>

                            <v-text-field
                                type="tel"
                                v-model="formFields.phone"
                                ref="phoneField"
                                label="Phone number *"
                                :rules="rules.phone"
                                class="rounded-0"
                                hint="Please use numbers only"
                                required
                                outlined
                            ></v-text-field>
                            <v-alert
                                v-if="errors && errors.phone"
                                type="error"
                            >{{ errors.phone[0] }}</v-alert>

                            <v-text-field
                                v-if="needsBookingCode"
                                v-model="formFields.bookingCode"
                                label="Booking code *"
                                class="rounded-0"
                                outlined
                            ></v-text-field>

                            <div class="widget-eligibility-form-field">
                                <v-checkbox
                                    v-model="formFields.newsletterConsent"
                                    :label="normalizedOptions.fields.newsletterConsent.label"
                                    class="rounded-0 mb-4"
                                    hide-details="auto"
                                    :required="normalizedOptions.fields.newsletterConsent.required"
                                    :rules="normalizedOptions.fields.newsletterConsent.required ? rules.newsletterConsent : []"
                                ></v-checkbox>
                            </div>

                            <div
                                class="widget-eligibility-form-field"
                                v-if="normalizedOptions.fields.newsSurveysConsent.show"
                            >
                                <v-checkbox
                                    v-model="formFields.newsSurveysConsent"
                                    :label="normalizedOptions.fields.newsSurveysConsent.label"
                                    class="rounded-0 mb-4"
                                    :required="normalizedOptions.fields.newsSurveysConsent.required"
                                    :rules="normalizedOptions.fields.newsSurveysConsent.required ? rules.newsSurveysConsent : []"
                                    hide-details="auto"
                                ></v-checkbox>
                            </div>

                            <div
                                class="widget-eligibility-form-field"
                                v-if="normalizedOptions.fields.partnersOffersConsent.show"
                            >
                                <v-checkbox
                                    v-model="formFields.partnersOffersConsent"
                                    :label="normalizedOptions.fields.partnersOffersConsent.label"
                                    class="rounded-0 mb-4"
                                    hide-details="auto"
                                    :required="normalizedOptions.fields.partnersOffersConsent.required"
                                    :rules="normalizedOptions.fields.partnersOffersConsent.required ? rules.partnersOffersConsent : []"
                                ></v-checkbox>
                            </div>

                            <div class="widget-eligibility-form-field">
                                <v-checkbox
                                    v-model="formFields.privacyPolicyAccepted"
                                    :label="normalizedOptions.fields.privacyPolicyAccepted.label"
                                    required
                                    class="rounded-0 mb-4"
                                    hide-details="auto"
                                    :rules="rules.privacyPolicyAccepted"
                                >
                                    <template v-slot:label>
                                        <div>
                                            I agree to {{ housingProviderName ? housingProviderName : 'the' }}
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on }">
                                                    <a
                                                        target="_blank"
                                                        :href="normalizedOptions.fields.privacyPolicyAccepted.linkToPolicy"
                                                        @click.stop
                                                        v-on="on"
                                                    >
                                                        privacy policy
                                                    </a>
                                                </template>
                                                Opens in new window
                                            </v-tooltip>
                                        </div>
                                    </template>

                                </v-checkbox>
                            </div>


                            <v-alert
                                v-if="errors && errors.bookingCode"
                                type="error"
                                class="mb-2"
                            >{{ errors.bookingCode[0] }}</v-alert>

                            <v-alert
                                v-if="errors && errors.appointment"
                                type="error"
                            >{{ errors.appointment[0] }}</v-alert>
                        </v-form>
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>

        <v-card-actions v-if="currentPhase == 'success' && (!inline || showFinishButton)">
            <v-spacer></v-spacer>
            <v-btn
                color="primary"
                @click="finishedBooking">
                Done
            </v-btn>
        </v-card-actions>

        <v-card-actions v-else :class="!modal && currentPhase == 'form' ? 'px-0' : ''">
            <template v-if="modal">
                <v-btn
                    color="dark darken-1"
                    text
                    @click="closeDialog"
                    :disabled="bookingLoading"
                >
                    Cancel
                </v-btn>
            </template>
            <v-spacer></v-spacer>

            <template v-if="currentPhase == 'form'">
                <v-btn
                    color="primary"
                    @click="submitBooking"
                    :loading="bookingLoading"
                    :disabled="!valid || bookingLoading"
                >
                    Book
                </v-btn>
            </template>

            <template v-if="currentPhase == 'confirmBooking'">
                <v-btn
                    v-if="skipForm === true"
                    color="primary"
                    @click="submitBooking"
                    :loading="bookingLoading"
                    :disabled="bookingLoading"
                >
                    Book
                </v-btn>
                <v-btn
                    v-else
                    color="primary"
                    @click="handleContinueButtonClick"
                    :disabled="bookingLoading"
                >
                    {{ nextButtonText }}
                </v-btn>
            </template>
        </v-card-actions>
    </v-card>
</template>

<script>
    import { defineComponent, ref, onMounted, computed, watch, nextTick } from '@vue/composition-api';
    import ListingService from "../../../../services/ListingService";
    import ProfileService from "../../../../services/ProfileService";
    import OpenDayService from "../../../../services/OpenDayService";
    import {forEach, has, includes, isNil, merge} from "lodash";

    export default defineComponent({
        name: 'Form',
        props: {
            inline: {
                type: Boolean,
                required: false,
                default: false
            },
            modal: {
                type: Boolean,
                required: false,
                default: true
            },
            listingId: {
                type: Number,
                required: false,
                default: null
            },
            listingSlug: {
                type: String,
                required: false,
                default: null
            },
            listingName: {
                type: String,
                required: false
            },
            listingLocation: {
                type: String,
                required: false
            },
            dates: {
                type: Array,
                required: false
            },
            minDate: {
                type: String,
                required: false
            },
            prefillFromCustomerObject: {
                type: Object,
                required: false,
                default: null
            },
            bookingCode: {
                type: String,
                required: false
            },
            skipForm: {
                type: Boolean,
                required: false,
                default: false
            },
            showFinishButton: {
                type: Boolean,
                required: false,
                default: false
            },
            enabledDates: {
                type: Array,
                required: false,
                default: function() {
                    return [];
                }
            },
            openDayId: {
                type: Number,
                required: false,
                default: null
            },
            openDayGroupId: {
                type: Number,
                required: false,
                default: null
            }
        },
        setup(props, context){
            // Store Options
            const application = ref({});
            const appointment = ref(null);
            const date = ref(null);
            const time = ref(null);
            const openDay = ref({});
            const success = ref(false);
            const error = ref(false);
            const errors = ref(null);
            const days = ref([]);
            const slots = ref([]);
            const times = ref([]);
            const loggedInProfile = ref(null);
            const listing = ref(null);
            const carouselPage = ref(0);

            const formFields = ref({
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                bookingCode: '',
                newsletterConsent: false,
                newsSurveysConsent: false,
                partnersOffersConsent: false,
                privacyPolicyAccepted: false
            });

            // Component Resources
            const currentPhase = ref('datepicker');

            // Validation
            const validationErrors = ref([]);
            const valid = ref(false);
            const showDatePicker = ref(false);
            const bookingLoading = ref(false);
            const rules = ref({
                firstName : [
                    v => !!v || 'First name is required',
                ],
                lastName : [
                    v => !!v || 'Last name is required',
                ],
                email : [
                    v => !!v || 'Email is required',
                    v => /.+@.+/.test(v) || 'Email must be valid',
                ],
                phone : [
                    v => !!v || 'Phone number is required',
                    v => /^[0-9]*$/.test(v) || 'Please enter a valid phone number'
                ],
                privacyPolicyAccepted: [
                    v => !!v || 'Required',
                ],
                newsletterConsent: [],
                newsSurveysConsent: [],
                partnersOffersConsent: []
            });

            const defaultSettings = ref({
                fields: {
                    privacyPolicyAccepted: {
                        label: `I agree to ${housingProviderName ? housingProviderName : 'the' }  privacy policy *`,
                        linkToPolicy: '/privacy-policy'
                    },
                    newsletterConsent: {
                        show: true,
                        label: 'I want to receive alerts about other relevant properties',
                        required: false,
                    },
                    newsSurveysConsent: {
                        show: true,
                        label: 'I want to sign up to your monthly newsletter',
                        required: false,
                    },
                    partnersOffersConsent: {
                        show: true,
                        label: `I want to receive information and offers from ${housingProviderName ? housingProviderName : 'the' } chosen partners`,
                        required: false,
                    },
                }
            });

            // Computed properties
            const normalizedOptions = computed(() => {
                return defaultSettings.value;
            });

            const housingProviderName = computed(() => {
                if(has(listing, 'value.housingProvider.name')) {
                    return listing.value.housingProvider.name;
                }
                return null;
            });

            const formattedDate = computed({
                get: () => {
                    if(date.value) {
                        const datetime = new Date(date.value + 'T00:00:00');
                        return new Intl.DateTimeFormat('en-GB').format(datetime);
                    }
                }
            });

            const startingDate = computed({
                get: () => {
                    if(props.minDate) {
                        return props.minDate;
                    } else if (days.value[0]) {
                        return days.value[0];
                    } else {
                        return
                    }
                }
            });

            const emailDisabled = computed({
                get: () => {
                    if(isNil(loggedInProfile.value)) {
                        return false;
                    }
                    return true;
                }
            });

            const inlineSelectors = computed({
                get: () => {
                    return !props.modal && (currentPhase.value == 'datepicker' || currentPhase.value == 'timepicker');
                }
            });

            const needsBookingCode = computed({
                get: () => {
                    if(has(listing, 'value.enablePrivateOpenDays')) {
                        if(listing.value.enablePrivateOpenDays) {
                            return true;
                        }
                    }
                    return false;
                }
            });

            const nextButtonText = computed({
                get: () => {
                    if(needsBookingCode.value === true) {
                        return 'Continue with appointment code';
                    }
                    return 'Continue';
                }
            });


            // Watch for date change
            watch(date, (currentValue, oldValue) => {
                if(currentValue)
                {
                    nextTick(() => formatTimeSlots(currentValue));
                    currentPhase.value = 'timepicker';
                } else {
                    time.value = null;
                    openDay.value = {};
                }
                carouselPage.value = 0;
            })

            /**
             * Data fetchers
             */

            function fetchAvailableDates(){
                if(props.listingId) {
                    getUpcomingOpenDaysByListingId();
                } else if (props.listingSlug) {
                    getUpcomingOpenDaysByListingSlug();
                } else if(props.openDayGroupId) {
                    getOpenDaysByGroupId(props.openDayGroupId);
                } else if(props.openDayId) {
                    getOpenDay(props.openDayId);
                } else  {
                    throw('No Listing/opendayId/opendayGroupId specified');
                }
            }

            function getOpenDay(openDayId) {
                OpenDayService.getOpenDayById(openDayId).then(function (response) {
                    const item = response.data;
                    if (props.enabledDates.includes(item.date)) {
                        if(item.slots.length > 0) {
                            // Push the dates to our days array
                            days.value.push(item.date);

                            // Store the slots against the date
                            slots.value[item.date] = item.slots;
                        }
                    } else {
                        if(item.slots.length > 0) {
                            // Push the dates to our days array
                            days.value.push(item.date);

                            // Store the slots against the date
                            slots.value[item.date] = item.slots;
                        }
                    }
                    if(!props.modal){
                        date.value = days.value[0];
                    }
                })
                .catch(function (error) {
                    console.error(error);
                })
            }

            function getOpenDaysByGroupId(groupId) {
                OpenDayService.getOpenDaysByGroupId(groupId).then(function (response) {
                    response.data.data.forEach(function(item){
                        if(has(props,'enabledDates') && props.enabledDates.length > 0) {
                            if (props.enabledDates.includes(item.date)) {
                                if(item.slots.length > 0) {
                                    // Push the dates to our days array
                                    days.value.push(item.date);

                                    // Store the slots against the date
                                    slots.value[item.date] = item.slots;
                                }
                            }
                        } else {
                            console.log(item);
                            if(item.slots.length > 0) {
                                // Push the dates to our days array
                                days.value.push(item.date);

                                // Store the slots against the date
                                slots.value[item.date] = item.slots;
                            }
                        }
                    });
                    if(!props.modal){
                        date.value = days.value[0];
                    }
                })
                .catch(function (error) {
                    console.error(error);
                })
            }

            function getUpcomingOpenDaysByListingId() {
                ListingService.getUpcomingOpenDaysByListingId(props.listingId)
                    .then(function (response) {
                        response.data.forEach(function(item){
                            if(has(props,'enabledDates') && props.enabledDates.length > 0) {
                                if (props.enabledDates.includes(item.date)) {
                                    if(item.slots.length > 0) {
                                        // Push the dates to our days array
                                        days.value.push(item.date);

                                        // Store the slots against the date
                                        slots.value[item.date] = item.slots;
                                    }
                                }
                            } else {
                                if(item.slots.length > 0) {
                                    // Push the dates to our days array
                                    days.value.push(item.date);

                                    // Store the slots against the date
                                    slots.value[item.date] = item.slots;
                                }
                            }
                        });
                        if(!props.modal){
                            date.value = days.value[0];
                        }
                    })
                    .catch(function (error) {
                        console.error(error);
                    })
            }

            function getUpcomingOpenDaysByListingSlug() {
                ListingService.getUpcomingOpenDaysByListingSlug(props.listingSlug)
                    .then(function (response) {
                        response.data.forEach(function(item){
                            if(has(props,'enabledDates') && props.enabledDates.length > 0) {
                                if (props.enabledDates.includes(item.date)) {
                                    if(item.slots.length > 0) {
                                        // Push the dates to our days array
                                        days.value.push(item.date);

                                        // Store the slots against the date
                                        slots.value[item.date] = item.slots;
                                    }
                                }
                            } else {
                                if(item.slots.length > 0) {
                                    // Push the dates to our days array
                                    days.value.push(item.date);

                                    // Store the slots against the date
                                    slots.value[item.date] = item.slots;
                                }
                            }
                        });
                        if(!props.modal){
                            date.value = days.value[0];
                        }
                    })
                    .catch(function (error) {
                        console.error(error);
                    })
            }

            function fetchAppointments() {
                ProfileService.getAppointments()
                    .then(function (response) {
                        context.emit('processAppointments', response.data.data);
                    })
                    .catch(function (error) {
                        console.error(error);
                    })
            }

            // Handle validation errors
            function handleErrors() {
                let errorPhases = [];
                let firstPhaseErrors = ['firstName', 'lastName', 'email', 'phone', 'bookingCode'];
                let secondPhaseErrors = ['slot'];
                forEach(errors.value, function(errValue, index) {
                    if(includes(firstPhaseErrors, index)) {
                        errorPhases.push('form');
                    }
                    if(includes(secondPhaseErrors, index)) {
                        errorPhases.push('datepicker');
                        date.value = null;
                        time.value = null;
                    }
                });
                if(errorPhases.length > 0) {
                    currentPhase.value = errorPhases[0];
                }
            }

            // Not used atm
            function submitLoggedIn() {
                const payload = {
                    slot: openDay.value.id,
                    bookingCode: formFields.value.bookingCode
                }

                ProfileService.bookViewingWithAuthentication(application.value.id, payload)
                    .then(function (response) {
                        if(response.data.status == 'confirmed')
                        {
                            success.value = true;
                        } else {
                            error.value = true;
                        }
                    })
                    .catch(function (error) {
                        console.error(error);
                        error.value = true;
                    })
            }

            function submitLoggedOut() {
                bookingLoading.value = true;
                errors.value = null;
                const additionalPayload = {
                    slot: openDay.value.id,
                }

                const payload = {...additionalPayload, ...formFields.value}

                ProfileService.bookViewingWithoutAuthentication(payload)
                    .then(function (response) {
                        if(response.data.status == 'confirmed')
                        {
                            success.value = true;
                            currentPhase.value = 'success';
                            context.emit("booked");
                            const target = document.getElementById('booking-container');
                            if(target) {
                                scrollToElement(target);
                            }
                        } else {
                            error.value = true;
                        }
                    })
                    .catch(function (error) {
                        if(error.response.data.errors) {
                            errors.value = error.response.data.errors;
                        }
                        handleErrors();
                        error.value = true;
                    }).then(() => {
                    bookingLoading.value = false;
                })
            }

            function submitBooking(){
                return submitLoggedOut();
            }

            function closeDialog() {
                date.value = null;
                currentPhase.value = 'datepicker';
                context.emit('closeDialog');
            }

            function finishedBooking() {
                context.emit("finishedBooking");
            }

            /**
             * Data formatters
             */
            function formatTimeSlots(date){
                times.value = [];
                const timeslots = slots.value[date];

                timeslots.forEach(function(item){
                    times.value.push(item);
                })
            }

            function chunkSlots(date){
                const chunkSize = 4;
                let chunks = [];
                for (let i = 0; i < slots.value[date].length; i += chunkSize) {
                    const chunk = slots.value[date].slice(i, i + chunkSize);
                    chunks.push(chunk);
                }
                return chunks;
            }

            function getTimeslotEnd(slot){
                const d = new Date("January 1, 2022 "+slot.slotTime);
                const end = new Date(d.getTime() + slot.slotDurationMin*60000);
                return end.getHours()+':'+(end.getMinutes() == '0' ? '00' : end.getMinutes());
            }


            /**
             * Picker functions
             */
            function allowedDates(val) {
                if (days.value.indexOf(val) !== -1) {
                    return true
                } else {
                    return false
                }
            }

            function selectTime(slot){
                time.value = slot.slotTime;
                openDay.value = {
                    'id': slot.id,
                    'duration': slot.slotDurationMin
                };
                currentPhase.value = 'confirmBooking';

                const target = document.getElementById('book-viewing');
                if(target) {
                    scrollToElement(target);
                }
            }

            function getError(fieldName) {
                return has(validationErrors.value.errors, fieldName);
            }

            function getErrorMessages(fieldName) {
                if(getError(fieldName)) {
                    return validationErrors.value.errors[fieldName];
                }
                return [];
            }

            function handleContinueButtonClick() {
                currentPhase.value = 'form';
            }

            function changeDate() {
                date.value = null;
                time.value = null;
                openDay.value = {};
                currentPhase.value = 'datepicker';
                carouselPage.value = 0;
            }

            // If logged in - populate form fields
            function initFormFields(profile) {
                if(has(profile, 'firstName')) {
                    formFields.value.firstName = profile.firstName;
                }
                if(has(profile, 'lastName')) {
                    formFields.value.lastName = profile.lastName;
                }
                if(has(profile, 'email')) {
                    formFields.value.email = profile.email;
                }
                if(has(profile, 'phone')) {
                    formFields.value.phone = profile.phone;
                }
            }

            function scrollToElement(target, duration = 300, offset = 0, easing = 'easeInOutCubic') {
                let options = {
                    duration: duration,
                    offset: offset,
                    easing: easing,
                }
                context.root.$vuetify.goTo(target, options);
            }

            function fetchListing() {
                if(props.listingId) {
                    ListingService.getListingFullDetailsById(props.listingId)
                        .then(response => {
                            if(has(response, 'data.listing')) {
                                listing.value = response.data.listing;
                            }
                        })
                        .catch(error => {
                            console.error(error);
                        });
                } else if (props.listingSlug) {
                    ListingService.getListingFullDetailsBySlug(props.listingSlug)
                        .then(response => {
                            if(has(response, 'data.listing')) {
                                listing.value = response.data.listing;
                            }
                        })
                        .catch(error => {
                            console.error(error);
                        });
                } else if (props.openDayId || props.openDayGroupId) {
                }
                else {
                    throw('No Listing specified');
                }
            }

            /**
             * Initialise
             */

            onMounted(() => {
                // Getting the listing
                fetchListing();
                // Getting the available dates
                fetchAvailableDates();

                // Check if user is logged in.
                // If so, we set the loggedInProfile ref value,
                // and we populate the booking form from the profile information with the help of the initFormFields method
                ProfileService.getProfile()
                    .then(response => {
                        loggedInProfile.value = response;
                        initFormFields(loggedInProfile.value);
                        fetchAppointments();
                    })
                    .catch(error => {
                        console.log(error);
                    })
                    .then(() => {
                        if(props.prefillFromCustomerObject) {
                            initFormFields(props.prefillFromCustomerObject);
                        }
                    })

                // setting up booking code if it's passed by a prop
                if(props.bookingCode) {
                    formFields.value.bookingCode = props.bookingCode;
                }
            })

            return {
                date,
                days,
                time,
                times,
                openDay,
                formFields,
                currentPhase,
                success,
                error,
                errors,
                listing,
                carouselPage,

                // Formatters
                chunkSlots,
                getTimeslotEnd,

                // Functions
                handleContinueButtonClick,
                getErrorMessages,
                changeDate,
                allowedDates,
                selectTime,
                submitBooking,
                closeDialog,
                finishedBooking,
                getError,

                // Computed
                formattedDate,
                startingDate,
                emailDisabled,
                inlineSelectors,
                needsBookingCode,
                nextButtonText,
                normalizedOptions,
                housingProviderName,

                // Validation
                validationErrors,
                valid,
                showDatePicker,
                bookingLoading,
                rules
            }
        }
    })
</script>
